import React, { useState, useEffect } from 'react';
import { X, Upload, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { productCategories, contacts } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const QuoteModal = ({ isOpen, onClose, preselectedResource = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    product_category: preselectedResource || '',
    quantity: '',
    message: '',
    file: null
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [honeypot, setHoneypot] = useState('');

  useEffect(() => {
    if (preselectedResource) {
      setFormData(prev => ({ ...prev, product_category: preselectedResource }));
    }
  }, [preselectedResource]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[+]?[\d\s-]{10,}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'file') {
      setFormData(prev => ({ ...prev, file: files[0] || null }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check
    if (honeypot) {
      console.log('Bot detected');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'file' && formData.file) {
          submitData.append('file', formData.file);
        } else if (key !== 'file') {
          submitData.append(key, formData[key]);
        }
      });

      const response = await fetch(`${BACKEND_URL}/api/quotes`, {
        method: 'POST',
        body: submitData
      });

      if (!response.ok) {
        let errorMessage = 'Failed to submit';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.detail || errorMessage;
        } catch {
          // Response wasn't JSON
        }
        throw new Error(errorMessage);
      }

      setSubmitStatus('success');
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        product_category: '',
        quantity: '',
        message: '',
        file: null
      });
      
      // Close modal after 3 seconds on success
      setTimeout(() => {
        onClose();
        setSubmitStatus(null);
      }, 3000);

    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      style={{ background: 'rgba(0, 0, 0, 0.6)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ background: 'var(--bg-primary)' }}
      >
        {/* Header */}
        <div 
          className="sticky top-0 flex items-center justify-between p-6 border-b z-10"
          style={{ background: 'var(--bg-primary)', borderColor: 'var(--border-light)' }}
        >
          <div>
            <h2 className="heading-2">Request a Quote</h2>
            <p className="body-small mt-1">We'll respond within 24-48 hours</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 transition-colors bg-transparent border-none cursor-pointer"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Success State */}
        {submitStatus === 'success' && (
          <div className="p-12 text-center">
            <CheckCircle size={64} className="mx-auto mb-4" style={{ color: 'var(--accent-teal)' }} />
            <h3 className="heading-2 mb-2">Thank You!</h3>
            <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              We've received your inquiry and will respond shortly.<br />
              A confirmation email has been sent to {formData.email || 'your email'}.
            </p>
          </div>
        )}

        {/* Error State */}
        {submitStatus === 'error' && (
          <div className="p-6 m-6" style={{ background: 'var(--accent-red-light)', borderLeft: '3px solid var(--accent-red)' }}>
            <div className="flex items-start gap-3">
              <AlertCircle size={20} style={{ color: 'var(--accent-red)' }} />
              <div>
                <p className="font-medium" style={{ color: 'var(--accent-red)' }}>Submission failed</p>
                <p className="body-small mt-1">Please try again or contact us directly:</p>
                <p className="body-small mt-2">
                  <a href={`mailto:${contacts[0].email}`} style={{ color: 'var(--accent-teal)' }}>
                    {contacts[0].email}
                  </a>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        {submitStatus !== 'success' && (
          <form onSubmit={handleSubmit} className="p-6">
            {/* Honeypot - hidden from users */}
            <div className="hp-field" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div className="form-group">
                <label className="form-label" htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Your full name"
                />
                {errors.name && <p className="form-error">{errors.name}</p>}
              </div>

              {/* Company */}
              <div className="form-group">
                <label className="form-label" htmlFor="company">Company *</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Company name"
                />
                {errors.company && <p className="form-error">{errors.company}</p>}
              </div>

              {/* Email */}
              <div className="form-group">
                <label className="form-label" htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="you@company.com"
                />
                {errors.email && <p className="form-error">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div className="form-group">
                <label className="form-label" htmlFor="phone">Phone *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="+91 XXXXX XXXXX"
                />
                {errors.phone && <p className="form-error">{errors.phone}</p>}
              </div>

              {/* Product Category */}
              <div className="form-group">
                <label className="form-label" htmlFor="product_category">Product Category</label>
                <select
                  id="product_category"
                  name="product_category"
                  value={formData.product_category}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="">Select a category</option>
                  {productCategories.map((cat, idx) => (
                    <option key={idx} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Quantity */}
              <div className="form-group">
                <label className="form-label" htmlFor="quantity">Estimated Quantity</label>
                <input
                  type="text"
                  id="quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="e.g., 10,000 pieces"
                />
              </div>
            </div>

            {/* Message */}
            <div className="form-group mt-6">
              <label className="form-label" htmlFor="message">Message / Requirements</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="form-textarea"
                placeholder="Describe your requirements, specifications, or attach a drawing..."
                rows={4}
              />
            </div>

            {/* File Upload */}
            <div className="form-group mt-6">
              <label className="form-label">Attach Drawing/Spec (Optional)</label>
              <div 
                className="border-2 border-dashed p-6 text-center cursor-pointer transition-colors hover:border-gray-400"
                style={{ borderColor: 'var(--border-medium)' }}
                onClick={() => document.getElementById('file-upload').click()}
              >
                <Upload size={24} className="mx-auto mb-2" style={{ color: 'var(--text-light)' }} />
                <p className="body-small">
                  {formData.file ? formData.file.name : 'Click to upload or drag and drop'}
                </p>
                <p className="body-small mt-1" style={{ color: 'var(--text-light)' }}>
                  PDF, DWG, DXF, PNG, JPG (max 10MB)
                </p>
                <input
                  type="file"
                  id="file-upload"
                  name="file"
                  onChange={handleChange}
                  className="hidden"
                  accept=".pdf,.dwg,.dxf,.png,.jpg,.jpeg"
                />
              </div>
            </div>

            {/* Submit */}
            <div className="mt-8 flex gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary flex-1"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default QuoteModal;
